/**
 * Данные сводки «С вашего прошлого визита» (§8.1).
 *
 * Отдельно от экрана по двум причинам. Первая: правило «системный пакет импорта
 * и пакеты-откаты исключены» должно жить в одном месте, иначе линза в таблице и
 * сводка однажды разойдутся в цифрах. Вторая: линзе «изменилось с прошлого
 * визита» в главной таблице нужен тот же набор брендов (`visitChangedIds` в
 * контексте фильтров) — она берёт его отсюда, а не считает сама.
 */
import type { ChangeSetDetail, ChangeSetSummary } from '@shared/types.ts'
import { api } from '../../lib/api.js'
import { digestSince } from './visit-mark.js'

/** Сколько страниц ленты читаем: 10 × 50 пакетов хватает на любой отпуск. */
const MAX_PAGES = 10
/** У скольких пакетов тянем подробности: дальше считаем по счётчикам пакетов. */
export const MAX_DETAILS = 60
const DETAIL_BATCH = 6

export interface DigestPayload {
  /** Пакеты окна без системного импорта и без откатов (§8.1). */
  packs: ChangeSetSummary[]
  details: Map<number, ChangeSetDetail>
  /** Подробности загружены не у всех пакетов: окно оказалось слишком большим. */
  truncated: boolean
}

function chunked<T>(list: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
  return out
}

export async function loadDigest(since: string): Promise<DigestPayload> {
  const all: ChangeSetSummary[] = []
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await api.changeSets({ from: since, page })
    all.push(...res.items)
    if (res.items.length === 0 || all.length >= res.total) break
  }

  // §8.1: системный импорт и откаты в сводку не попадают.
  const packs = all.filter((pack) => !pack.isSystem && pack.revertsChangeSetId === null)

  const details = new Map<number, ChangeSetDetail>()
  const targets = packs.slice(0, MAX_DETAILS)
  for (const batch of chunked(targets, DETAIL_BATCH)) {
    const loaded = await Promise.all(batch.map((pack) => api.changeSet(pack.id)))
    for (const detail of loaded) details.set(detail.id, detail)
  }

  return { packs, details, truncated: packs.length > targets.length }
}

/** Бренды, которых коснулись правки окна: набор для линзы в таблице (§8.1). */
export function visitChangedBrandIds(payload: DigestPayload): Set<number> {
  const ids = new Set<number>()
  for (const detail of payload.details.values()) {
    for (const change of detail.changes) {
      if (change.brandId != null) ids.add(change.brandId)
    }
  }
  return ids
}

/**
 * Готовый набор для `visitChangedIds` в контексте фильтров главного экрана.
 * Метку визита не сдвигает: её сдвигает только «Открыть таблицу» (§8.1).
 */
export async function loadVisitChangedBrandIds(userId: number): Promise<Set<number>> {
  const payload = await loadDigest(digestSince(userId).since)
  return visitChangedBrandIds(payload)
}
